import * as I from 'immutable';
import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {ApiImageryContract} from 'app/modules/Remote/Project';
import {
  useCurrentImageryContract,
  useImageryContracts,
  useImageryPurchases,
} from 'app/providers/ImageryContractProvider';
import {StatusMaybe} from 'app/utils/hookUtils';
import {paysAtCheckout} from 'app/utils/organizationUtils';
import * as routeUtils from 'app/utils/routeUtils';

import View, {Props} from './view';

const NO_CONTRACT: StatusMaybe<ApiImageryContract | null> = {status: 'some', value: null};

const ProjectDashboardSwitchDataProvider: React.FunctionComponent<
  React.PropsWithChildren<
    Omit<Props, 'getImageryContracts' | 'getCurrentImageryContract' | 'getImageryPurchases'>
  >
> = (props) => {
  // Keep these at this level so that they can be shared by PropertyOverview and
  // MonitorProjectView.
  const [getImageryContracts] = useImageryContracts();
  const [getContract] = useCurrentImageryContract();
  const [getImageryPurchases] = useImageryPurchases();

  // An org that pays at checkout bills none of its contracts, so its orders never wait on a lookup.
  const orgPaysAtCheckout = paysAtCheckout(props.organization);
  const getCurrentImageryContract = React.useMemo(
    () => (orgPaysAtCheckout ? () => NO_CONTRACT : getContract),
    [orgPaysAtCheckout, getContract]
  );

  return (
    <View
      {...props}
      getImageryContracts={getImageryContracts}
      getCurrentImageryContract={getCurrentImageryContract}
      getImageryPurchases={getImageryPurchases}
    />
  );
};

// HACK(fiona): This matches the behavior of Redux’s connect by not re-rendering
// the component if the props are all shallowly equal.
//
// Currently necessary to keep the overview page’s row-clicking working because
// of a number of issues with how it causes a re-render on row focus, which
// breaks click events due to issues with reconciliation (the mouseDown and
// mouseUp events end up happening in different identical DOM elements).
export default React.memo(ProjectDashboardSwitchDataProvider);

export interface FeatureSelection {
  organizationId: string;
  projectId: string;
  featureIds?: I.Set<number | string> | null;
}

/**
 * Hook that returns a function to navigate to the correct URL for a project/feature
 * collection/features location.
 */
export function useChangeSelection() {
  const navigate = useNavigate();
  const location = useLocation();

  return React.useCallback(
    (selection: FeatureSelection) => {
      const {organizationId, projectId, featureIds = I.Set<number | string>()} = selection;

      const nextPathname = routeUtils.makeProjectDashboardUrl(
        organizationId,
        projectId,
        featureIds ? 'map' : undefined,
        featureIds?.toArray() ?? undefined
      );

      // The run selection is feature-scoped; don't carry it to a different location.
      const search =
        nextPathname === location.pathname
          ? location.search
          : routeUtils.removeAgentRunIdFromSearch(location.search);

      const path = nextPathname + search;
      const currentPath = location.pathname + location.search;

      if (path != currentPath) {
        navigate(path);
      }
    },
    [navigate, location]
  );
}

/**
 * @deprecated Use useChangeSelection hook instead
 * Helper function to navigate to the correct URL for a project/feature
 * collection/features location.
 * This function uses window.location.href for navigation as a fallback.
 * Components should use the useChangeSelection hook instead.
 */
export function changeSelection(selection: FeatureSelection) {
  // Fallback implementation using window.location for backward compatibility
  // This should be replaced with useChangeSelection hook in all components
  const {organizationId, projectId, featureIds = I.Set<number | string>()} = selection;

  const queryString = window.location.search;

  const path =
    routeUtils.makeProjectDashboardUrl(
      organizationId,
      projectId,
      featureIds ? 'map' : undefined,
      featureIds?.toArray() ?? undefined
    ) + queryString;

  const currentPath = window.location.pathname + queryString;

  if (path != currentPath) {
    // Use window.location.href for navigation - this will cause a full page reload
    // but ensures navigation works correctly with React Router v6
    window.location.href = path;
  }
}
